

import type { AppointmentRequest, AvailabilityWindow } from "@/lib/slotting/types";
import { useTranslations } from "next-intl";
import { useUserLocale } from "@/hooks/useUserLocale";
import { useEventTimezone } from "@/providers/EventProvider";

interface RequestEntryProps {
  request: AppointmentRequest;
  treatmentMap: Map<string, string>;
  offeringName?: string;
  onPreview: (request: AppointmentRequest) => void;
}

// startDt/endDt are real UTC instants — render in the event tz, not browser tz.
function formatDate(dt: string, locale: string, tz: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleDateString(locale, {
      timeZone: tz,
      day: "numeric",
      month: "short",
    });
  } catch {
    return dt;
  }
}

function formatTimeRange(
  startDt: string,
  endDt: string,
  locale: string,
  tz: string,
): string {
  try {
    const s = new Date(startDt);
    const e = new Date(endDt);
    const fmt = (d: Date) =>
      d.toLocaleTimeString(locale, {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
      });
    return `${fmt(s)} – ${fmt(e)}`;
  } catch {
    return "";
  }
}

function WindowEntry({
  window,
  locale,
  tz,
}: {
  window: AvailabilityWindow;
  locale: string;
  tz: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <p className="body-4-regular font-tertiary">
        {formatDate(window.startDt, locale, tz)}
      </p>
      <p className="body-4-regular font-tertiary shrink-0">
        {formatTimeRange(window.startDt, window.endDt, locale, tz)}
      </p>
    </div>
  );
}

export const RequestEntry: React.FC<RequestEntryProps> = ({
  request,
  treatmentMap,
  offeringName,
  onPreview,
}) => {
  const ts = useTranslations("slotting");
  const locale = useUserLocale();
  const tz = useEventTimezone();

  const treatmentNames = request.treatmentIds
    ?.map((id) => treatmentMap.get(id))
    .filter(Boolean) as string[] | undefined;

  const hasTreatments = treatmentNames && treatmentNames.length > 0;
  const hasAvailabilities = request.availability?.length > 0;

  return (
    <li
      onClick={() => onPreview(request)}
      className="rounded-xl px-3 py-2.5 border flex flex-col gap-1.5 outline-tertiary cursor-pointer hover:surface-secondary transition-colors"
    >
      {/* Offering name */}
      {offeringName && (
        <p className="body-3-medium">{offeringName}</p>
      )}

      {/* Treatments */}
      <p className={`body-4-regular ${hasTreatments ? "font-secondary" : "font-tertiary"}`}>
        {hasTreatments ? treatmentNames.join(", ") : ts("noTreatmentsSpecified")}
      </p>

      {/* Availability windows */}
      {hasAvailabilities ? (
        <div className="flex flex-col gap-0.5">
          {request.availability.map((w, i) => (
            <WindowEntry window={w} locale={locale} tz={tz} key={i} />
          ))}
        </div>
      ) : (
        <p className="body-4-regular font-tertiary">{ts("noAvailabilities")}</p>
      )}

      {/* Notes */}
      {request.notes && (
        <p className="body-4-regular font-tertiary truncate">{request.notes}</p>
      )}
    </li>
  );
};
